import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BookOpen, Calendar, Clock, User } from "lucide-react";
import { format } from "date-fns";

interface TestInvitation {
  id: string;
  test_id: string;
  email: string;
  sent_at: string | null;
  completed_at: string | null;
  score: number | null;
  invitation_code: string | null;
  instructor_tests: {
    title: string;
    description: string | null;
    subject: string;
    grade: string;
    topic: string;
    time_limit: number | null;
    total_questions: number;
    instructor_id: string;
  };
}

const TestInvitations = () => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<TestInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.email) {
      fetchInvitations();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchInvitations = async () => {
    try {
      // Fetch only the test_invitations data first
      const { data, error } = await supabase
        .from('test_invitations')
        .select('*')
        .eq('email', user?.email)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      // Set invitations without instructor_tests data for now
      const invitationsWithoutTests = (data || []).map(invitation => ({
        ...invitation,
        instructor_tests: null // We'll handle this in the UI
      }));

      setInvitations(invitationsWithoutTests);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Failed to load test invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (invitation: TestInvitation) => {
    try {
      // Check if test is already completed
      if (invitation.completed_at) {
        toast.error('You have already completed this test');
        return;
      }
      
      // Fetch test data first
      const { data: testData, error } = await supabase
        .from('test_invitations')
        .select(`
          *,
          instructor_tests (
            title,
            description,
            subject,
            grade,
            topic,
            time_limit,
            total_questions
          )
        `)
        .eq('id', invitation.id)
        .single();

      if (error || !testData) {
        toast.error('Failed to load test data');
        return;
      }
      
      // Navigate to the test with invitation data
      navigate(`/test/invitation`, {
        state: { 
          invitation: testData,
          testData: testData.instructor_tests 
        }
      });
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error('Failed to start test');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Test Invitations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invitations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Test Invitations
          </CardTitle>
          <CardDescription>
            Tests assigned to you by instructors will appear here
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No test invitations yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Test Invitations ({invitations.length})
        </CardTitle>
        <CardDescription>
          Complete the tests assigned to you by your instructors
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {invitations.map((invitation) => {
          return (
            <Card key={invitation.id} className="border-l-4 border-l-primary">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">
                        Test Invitation
                      </h3>
                      {invitation.completed_at ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Completed
                        </Badge>
                      ) : (
                        <Badge variant="default">
                          Pending
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-muted-foreground text-sm">
                      You have been invited to take a test. Click "Start Test" to view test details and begin.
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        Test details will be shown when you start
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Instructor
                      </div>
                    </div>
                    
                    {invitation.sent_at && (
                      <p className="text-xs text-muted-foreground">
                        Invited: {format(new Date(invitation.sent_at), 'MMM dd, yyyy')}
                      </p>
                    )}
                    
                    {invitation.completed_at && invitation.score !== null && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-green-50">
                          Score: {invitation.score}%
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Completed: {format(new Date(invitation.completed_at), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4">
                    {!invitation.completed_at ? (
                      <Button 
                        onClick={() => handleStartTest(invitation)}
                        size="sm"
                      >
                        Start Test
                      </Button>
                    ) : (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleStartTest(invitation)}
                      >
                        View Results
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default TestInvitations;